/**
 * Comunicaciones: campañas de correo a suscriptores y propietarios.
 *
 * Tipos copiados de `server/src/services/comunicaciones/esquema.ts` (cliente y
 * servidor no comparten paquete). Si cambian allí, hay que traerlos aquí.
 */

export const IDIOMAS = ['es', 'en', 'fr', 'de'] as const;
export type Idioma = (typeof IDIOMAS)[number];

export const NOMBRE_IDIOMA: Record<Idioma, string> = { es: 'Español', en: 'Inglés', fr: 'Francés', de: 'Alemán' };

export const ORIGENES_SUSCRIPTOR = ['guest_portal', 'checkin', 'web'] as const;
export type OrigenSuscriptor = (typeof ORIGENES_SUSCRIPTOR)[number];
export const NOMBRE_ORIGEN: Record<OrigenSuscriptor, string> = { guest_portal: 'Portal del huésped', checkin: 'Check-in', web: 'tusereno.es' };

export type EstadoCampana = 'borrador' | 'programada' | 'enviando' | 'pausada' | 'enviada' | 'cancelada';
export type Canal = 'email' | 'whatsapp';
export type EstadoEnvio = 'pendiente' | 'enviando' | 'enviado' | 'error' | 'omitido' | 'interrumpido';
export type TipoDestinatario = 'suscriptor' | 'propietario';

export type Bloque =
  | { id: string; tipo: 'titular'; texto: string; tamano: 'grande' | 'mediano' }
  | { id: string; tipo: 'texto'; texto: string; alineacion: 'izquierda' | 'centro' }
  | { id: string; tipo: 'imagen'; url: string; alt: string; enlace: string; ancho: 'completo' | 'medio' }
  | { id: string; tipo: 'boton'; texto: string; url: string; alineacion: 'izquierda' | 'centro' }
  | { id: string; tipo: 'separador' }
  | { id: string; tipo: 'espacio'; alto: 16 | 32 | 48 }
  /** HTML pegado tal cual; el servidor lo limpia (sin scripts, marcos ni formularios) al pintarlo. */
  | { id: string; tipo: 'html'; html: string };
export type TipoBloque = Bloque['tipo'];

export interface Version {
  asunto: string;
  preheader: string;
  bloques: Bloque[];
}

export interface Audiencia {
  suscriptores: { incluir: boolean; origenes: OrigenSuscriptor[]; idiomas: Idioma[] };
  propietarios: { incluir: boolean; estados: ('active' | 'inactive')[]; incluirSecundario: boolean };
}

export interface ParametroWhatsApp {
  tipo: 'nombre' | 'texto';
  /** Texto fijo, o el valor por defecto del nombre si el destinatario no lo tiene. */
  valor: string;
}

export interface WhatsAppCampana {
  plantilla: string;
  /** Por hueco: `body.1`, `header.1`, `boton.0`… */
  parametros: Record<string, ParametroWhatsApp>;
  cabeceraImagen: string;
}

export interface ResumenCampana {
  total: number;
  enviados: number;
  errores: number;
  omitidos: number;
  entregados: number;
  noEntregados: number;
  abiertos: number;
  clics: number;
  bajas: number;
  leidos?: number;
  respuestas?: number;
}

export interface Campana {
  id: string;
  /** Sin el campo, es de correo (las anteriores a WhatsApp). */
  canal?: Canal;
  nombre: string;
  estado: EstadoCampana;
  audiencia: Audiencia;
  remitente: { direccion: string; responderA: string };
  idiomaReserva: Idioma;
  versiones: Partial<Record<Idioma, Version>>;
  whatsapp?: WhatsAppCampana;
  programadaPara?: string;
  creadaPor: string;
  creadaEn: string;
  actualizadaEn: string;
  lanzada?: { en: string; por: string; total: number };
  enviadaEn?: string;
  detalle?: string;
  resumen?: ResumenCampana;
}

/** Lo que se manda al guardar un borrador. */
export type CampanaEditable = Pick<Campana, 'nombre' | 'audiencia' | 'remitente' | 'idiomaReserva' | 'versiones' | 'whatsapp'>;

export const canalDe = (c: Pick<Campana, 'canal'>): Canal => c.canal || 'email';

export interface Contador { primera: string; ultima: string; veces: number }

export interface Envio {
  id: string;
  email: string;
  nombre: string;
  idioma: string;
  idiomaEnviado?: Idioma;
  tipo: TipoDestinatario;
  refId: string;
  telefono?: string;
  canal?: Canal;
  /** WhatsApp: cuándo lo leyó y cuántas veces contestó. */
  leido?: string;
  respuestas?: number;
  estado: EstadoEnvio;
  enviadoEn?: string;
  detalle?: string;
  entrega?: { estado: string; ok: boolean; fecha?: string; detalle?: string };
  abierto?: Contador;
  clics?: Contador & { enlaces: Record<string, number> };
  baja?: { en: string; via: 'enlace' | 'un-clic' | 'respuesta' };
}

export interface Recuento {
  total: number;
  porTipo: Partial<Record<TipoDestinatario, number>>;
  porIdioma: Record<string, number>;
  descartes: { baja: number; duplicado: number; sinEmail: number };
}

export interface Revision {
  errores: string[];
  /** No impiden lanzar, pero hay que confirmarlos (WhatsApp: idiomas de la plantilla sin aprobar). */
  avisos: string[];
  recuento: Recuento;
  /** Cuántos recibirán cada versión (la propia o la de reserva). */
  porVersion: Partial<Record<Idioma, number>>;
}

export interface AvisoVersion { nivel: 'error' | 'aviso'; texto: string }

export interface VistaPrevia {
  asunto: string;
  preheader: string;
  html: string;
  texto: string;
  avisos: AvisoVersion[];
}

export interface FilaEnlace { idioma: Idioma; n: number; url: string; clics: number; personas: number }

export interface Baja {
  /** El email, o `wa:+34…` si es una baja solo de WhatsApp. */
  id: string;
  email: string;
  telefono?: string;
  en: string;
  origen: 'enlace' | 'un-clic' | 'manual' | 'rebote' | 'respuesta';
  tipo?: TipoDestinatario;
  campanaId?: string;
  detalle?: string;
  por?: string;
}

export interface AjustesComunicaciones {
  remitentes: { direccion: string; nombre: string }[];
  responderAPorDefecto: string;
  porMinuto: number;
  porHora: number;
  whatsapp: {
    wabaId: string;
    phoneNumberId: string;
    appId: string;
    tokenVerificacion: string;
    respuestaAutomatica: Partial<Record<Idioma, string>>;
  };
}

// ── WhatsApp ────────────────────────────────────────────────────────────────

export interface HuecoPlantilla {
  clave: string;
  donde: 'cabecera' | 'cuerpo' | 'botón';
  nombre: string;
  imagen?: boolean;
}

export interface PlantillaResumen {
  nombre: string;
  categoria: string;
  /** En orden: el español delante. */
  idiomas: { idioma: string; estado: string; motivo?: string; previa: PreviaWhatsApp }[];
  huecos: HuecoPlantilla[];
  aprobada: boolean;
  /** Creada desde el CRM: solo esas se pueden borrar o ampliar con otro idioma. */
  delCrm: boolean;
  /** Solo en las del CRM: para campañas o para escribir a un lead desde su ficha. */
  uso?: UsoPlantilla;
  previa: PreviaWhatsApp;
}

/** Lo que se manda a Meta para crear una versión (un idioma) de una plantilla. */
export interface BorradorPlantilla {
  uso: UsoPlantilla;
  nombre: string;
  idioma: Idioma;
  cabecera: { tipo: 'ninguna' } | { tipo: 'texto'; texto: string; ejemplo: string } | { tipo: 'imagen'; url: string };
  cuerpo: string;
  ejemplos: string[];
  pie: string;
  botones: ({ tipo: 'enlace'; texto: string; url: string; ejemplo: string } | { tipo: 'respuesta'; texto: string } | { tipo: 'baja'; texto: string })[];
}

export const TEXTO_BOTON_BAJA: Record<Idioma, string> = {
  es: 'Dejar de recibir',
  en: 'Stop promotions',
  fr: 'Arrêter les promos',
  de: 'Werbung abbestellen',
};

export const ESTADO_PLANTILLA: Record<string, { texto: string; clase: string }> = {
  APPROVED: { texto: 'Aprobada', clase: 'bg-emerald-100 text-emerald-700' },
  PENDING: { texto: 'En revisión', clase: 'bg-amber-100 text-amber-700' },
  REJECTED: { texto: 'Rechazada', clase: 'bg-red-100 text-red-700' },
  PAUSED: { texto: 'Pausada', clase: 'bg-orange-100 text-orange-700' },
  DISABLED: { texto: 'Desactivada', clase: 'bg-slate-100 text-slate-600' },
};

export interface PreviaWhatsApp {
  cabecera?: string;
  imagen?: string;
  cuerpo: string;
  pie?: string;
  botones: { tipo: string; texto: string; url?: string }[];
}

export interface EstadoWhatsApp {
  ids: { wabaId: string; phoneNumberId: string; appId: string };
  token: boolean;
  appSecret: boolean;
  numero: { display_phone_number?: string; verified_name?: string; quality_rating?: string; messaging_limit_tier?: string; name_status?: string } | null;
  errorNumero: string | null;
  webhook: { url: string; tokenVerificacion: string; campos: string[] };
  ultimoAvisoEn: string | null;
  versionApi: string;
}

export interface Conversacion {
  telefono: string;
  nombre: string;
  email?: string;
  idioma: string;
  campanaId?: string;
  campanaNombre?: string;
  /** Si es un lead al que se escribió desde su ficha. */
  leadId?: string;
  leadNombre?: string;
  ultimoMensajeEn: string;
  ultimoTexto: string;
  ultimoEntranteEn?: string;
  sinLeer: number;
  atendida: boolean;
  baja?: string;
  /** El último «Procesar conversación» aceptado: lo siguiente se procesa desde `hasta`. */
  procesada?: { hasta: string; resumen: string; en: string; por: string };
}

export interface MensajeWhatsApp {
  id: string;
  telefono: string;
  direccion: 'entrante' | 'saliente';
  texto: string;
  en: string;
  estado?: string;
  detalle?: string;
  automatico?: boolean;
  por?: string;
  campanaId?: string;
  /** Si fue una plantilla (las que abren conversación con un lead). */
  plantilla?: string;
}

export interface HiloWhatsApp {
  conversacion: Conversacion;
  mensajes: MensajeWhatsApp[];
  ventana: { abierta: boolean; hasta: string | null };
}

/** El chat de WhatsApp de un lead: sin conversación todavía es lo normal antes del primer mensaje. */
export interface WhatsAppDeLead {
  /** En formato de WhatsApp (+34…), o null si el de la ficha no vale. */
  telefono: string | null;
  telefonoOriginal: string;
  conversacion: Conversacion | null;
  mensajes: MensajeWhatsApp[];
  ventana: { abierta: boolean; hasta: string | null };
  /** Cuándo pidió no recibir más mensajes por WhatsApp. */
  baja: string | null;
}

export type UsoPlantilla = 'campanas' | 'leads' | 'portal';

/** Lo que la IA propone apuntar de una conversación (la misma lectura que las llamadas). */
export interface PropuestaConversacion {
  resumen: string;
  puntosClave: string[];
  objeciones: string[];
  siguientePaso: string | null;
  temperatura: 'fria' | 'templada' | 'caliente' | null;
  faseSugerida: { id: string; porQue: string } | null;
  /** Solo los datos que la ficha no tiene: lo escrito no se pisa. */
  campos: Partial<Record<'email' | 'propertyAddress' | 'propertyZone' | 'propertyType' | 'propertyBedrooms' | 'propertyCurrentState' | 'sourceDetail', string | number>>;
  modelo: string;
  at: string;
}

export interface ProcesoConversacion {
  propuesta: PropuestaConversacion;
  desde: string;
  hasta: string;
  mensajes: number;
  fases: { id: string; nombre: string }[];
  faseActual: string | null;
}

export const DOMINIO_ENVIO = 'news.tusereno.es';

// ── Presentación ────────────────────────────────────────────────────────────

export const ESTADO_CAMPANA: Record<EstadoCampana, { texto: string; clase: string }> = {
  borrador: { texto: 'Borrador', clase: 'bg-slate-100 text-slate-600' },
  programada: { texto: 'Programada', clase: 'bg-sky-100 text-sky-700' },
  enviando: { texto: 'Enviando', clase: 'bg-amber-100 text-amber-700' },
  pausada: { texto: 'Pausada', clase: 'bg-orange-100 text-orange-700' },
  enviada: { texto: 'Enviada', clase: 'bg-emerald-100 text-emerald-700' },
  cancelada: { texto: 'Cancelada', clase: 'bg-red-100 text-red-700' },
};

export const ORIGEN_BAJA: Record<Baja['origen'], string> = {
  enlace: 'Enlace del correo',
  'un-clic': 'Botón de Gmail/Yahoo',
  manual: 'A mano',
  rebote: 'Rebote',
  respuesta: 'Contestó BAJA por WhatsApp',
};

/** «12 %», o «—» si no hay base. */
export function porcentaje(parte: number, total: number): string {
  if (!total) return '—';
  return `${Math.round((parte / total) * 100)} %`;
}

export function idBloque(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function bloqueNuevo(tipo: TipoBloque): Bloque {
  const id = idBloque();
  switch (tipo) {
    case 'titular': return { id, tipo, texto: '', tamano: 'grande' };
    case 'texto': return { id, tipo, texto: '', alineacion: 'izquierda' };
    case 'imagen': return { id, tipo, url: '', alt: '', enlace: '', ancho: 'completo' };
    case 'boton': return { id, tipo, texto: '', url: '', alineacion: 'izquierda' };
    case 'separador': return { id, tipo };
    case 'espacio': return { id, tipo, alto: 32 };
    case 'html': return { id, tipo, html: '' };
  }
}

export function versionVacia(): Version {
  return { asunto: '', preheader: '', bloques: [] };
}

/** Una versión está empezada si tiene asunto o algún bloque. Misma regla que el servidor. */
export function versionEmpezada(v?: Version): v is Version {
  return !!v && (v.asunto.trim() !== '' || v.bloques.length > 0);
}

/** Copia de una versión con ids de bloque nuevos, para empezar otro idioma a partir de ella. */
export function copiarVersion(v: Version): Version {
  return { ...v, bloques: v.bloques.map(b => ({ ...b, id: idBloque() })) };
}
